/* eslint-disable react/jsx-props-no-spreading */
import React from "react";

import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import StatsAPI from "../../../api/StatsAPI";
import { API_DATE_FORMAT } from "../../../constants/dateFormat";
import {
  EXPORT_SCHEDULING_REPORT_FORM_ID,
  EXPORT_SCHEDULING_REPORT_MODAL_ID,
} from "../../../constants/fast";
import { downloadFile } from "../../../utils/file";
import DateRangePicker from "../../form/DateRangePicker";
import Select from "../../form/Select";

import type { JSX } from "react";

import type { GetSchedulesResponse } from "../../../api/FastAPI.types";
import type {
  AsRunReportType,
  DownloadAsRunReportParams,
} from "../../../api/StatsAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

export interface FormValues {
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  schedule: {
    value: string;
    label: string;
  };
  reportType: {
    value: AsRunReportType;
    label: string;
  };
}

interface CalendarExportAsRunModalProps {
  scheduleList?: GetSchedulesResponse;
}

function CalendarExportAsRunForm({
  scheduleList,
}: CalendarExportAsRunModalProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const { channelId } = useParams<FASTChannelRoutesParams>();

  const methods = useForm<FormValues>({
    defaultValues: {
      dateRange: {
        startDate: null,
        endDate: null,
      },
    },
  });

  const { mutate: downloadAsRunReport } = useMutation({
    mutationFn: async ({
      periodStart,
      periodEnd,
      scheduleId,
      reportType,
    }: Omit<DownloadAsRunReportParams, "channelId">) =>
      StatsAPI.downloadAsRunReport({
        channelId,
        periodStart,
        periodEnd,
        scheduleId,
        reportType,
      }),
    onSuccess: (data, { periodStart, periodEnd, reportType }) => {
      downloadFile({
        payload: data,
        filename: `as_run_report-${reportType}-${periodStart}-${periodEnd}`,
        fileExtension: "csv",
      });
    },
  });

  const handleSubmit = (data: FormValues): void => {
    const periodStart = moment(data.dateRange.startDate)?.format(
      API_DATE_FORMAT,
    );
    const periodEnd = moment(data.dateRange.endDate)?.format(API_DATE_FORMAT);
    const scheduleId = data.schedule.value;
    const reportType = data.reportType.value;

    downloadAsRunReport({ periodStart, periodEnd, scheduleId, reportType });
  };

  const scheduleOptionList = scheduleList?.data.map((schedule) => ({
    label: schedule.name,
    value: schedule.uuid,
  }));

  const reportTypeOptionList = [
    {
      label: t("asRunReportType.daily"),
      value: "all_schedule_played",
    },
    {
      label: t("asRunReportType.media"),
      value: "sum_schedule_played",
    },
  ];

  return (
    <FormProvider {...methods}>
      <form
        id={EXPORT_SCHEDULING_REPORT_FORM_ID}
        onSubmit={methods.handleSubmit((data) => handleSubmit(data))}
      >
        <div className="flex flex-col gap-[0.78125rem]">
          <div className="font-semibold">{t("selectTimerange")}</div>
          <DateRangePicker
            isRequired
            className="mb-[0.78125rem] flex gap-[0.9375rem]"
            endLabel={t("to")}
            maxDate={new Date()}
            name="dateRange"
            portalId={EXPORT_SCHEDULING_REPORT_MODAL_ID}
            startLabel={t("from")}
          />
          <Select
            isCompact
            isPortal
            isRequired
            className="mb-[0.78125rem]"
            label={t("selectSchedule")}
            name="schedule"
            options={scheduleOptionList}
          />
          <Select
            isCompact
            isPortal
            isRequired
            className="mb-[0.78125rem]"
            label={t("selectAsRunReportType")}
            name="reportType"
            options={reportTypeOptionList}
          />
        </div>
      </form>
    </FormProvider>
  );
}

export default CalendarExportAsRunForm;
